import { Link } from "react-router-dom";
import { useAuthContext } from "../../contexts/AuthContext";
import { useSubscription } from "../../hooks/useSubscription";
import { 
  AcademicCapIcon, 
  ChartBarIcon, 
  CogIcon,
  PhotoIcon, 
  UserIcon,
  PlusCircleIcon,
  RocketIcon
} from "../../components/icons/OutlineIcons";
import { ArrowLeft } from "lucide-react";

export default function InstructorHub() {
  const { user } = useAuthContext();
  const { plan } = useSubscription();

  const menuItems = [
    {
      title: "สร้างคอร์สเรียน",
      description: "เริ่มสร้างคอร์สออนไลน์ของคุณ",
      icon: PlusCircleIcon,
      link: "/instructor/courses/new",
      color: "from-blue-500 to-pink-500",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      title: "ตั้งค่าโปรไฟล์",
      description: "แก้ไขข้อมูลส่วนตัวและผลงาน",
      icon: UserIcon,
      link: "/instructor/settings",
      color: "from-blue-500 to-cyan-500",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      title: "ตั้งค่าหน้าปก",
      description: "ปรับแต่งหน้าปกโปรไฟล์ของคุณ",
      icon: PhotoIcon,
      link: "/instructor/settings?tab=cover",
      color: "from-green-500 to-emerald-500",
      iconBg: "bg-green-100",
      iconColor: "text-green-600"
    },
    {
      title: "Dashboard",
      description: "ดูภาพรวมคอร์สและนักเรียน",
      icon: ChartBarIcon,
      link: "/instructor/dashboard",
      color: "from-blue-500 to-red-500",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      title: "Analytics",
      description: "วิเคราะห์สถิติและรายได้",
      icon: ChartBarIcon,
      link: "/instructor/analytics",
      color: "from-indigo-500 to-blue-500",
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600"
    },
    {
      title: "ตั้งค่าระบบ",
      description: "จัดการการตั้งค่าทั่วไป",
      icon: CogIcon,
      link: "/instructor/settings?tab=general",
      color: "from-gray-500 to-slate-500",
      iconBg: "bg-gray-100",
      iconColor: "text-gray-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-pink-600 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Link to="/instructor/dashboard" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 font-semibold transition-colors">
            <ArrowLeft className="w-5 h-5" />
            กลับไปหน้า Dashboard
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <RocketIcon className="w-12 h-12" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">
                ยินดีต้อนรับ, {user?.email?.split('@')[0]}!
              </h1>
              <p className="text-xl text-blue-100 mt-2">
                เริ่มต้นสร้างคอร์สออนไลน์ของคุณได้เลย
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Quick Start Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-textDark mb-3">เริ่มต้นอย่างรวดเร็ว</h2>
          <p className="text-textLight text-lg mb-8">
            เลือกสิ่งที่คุณต้องการทำวันนี้
          </p>

          {/* Menu Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {menuItems
              .filter(item => {
                // Hide Dashboard for Free tier
                if (item.link === "/instructor/dashboard" && !plan?.features?.hasDashboard) {
                  return false;
                }
                return true;
              })
              .map((item, index) => (
                <Link
                  key={index}
                  to={item.link}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2"
                >
                  <div className={`h-2 bg-gradient-to-r ${item.color}`}></div>
                  <div className="p-6">
                    <div className={`${item.iconBg} w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className={`w-8 h-8 ${item.iconColor}`} />
                    </div>
                    <h3 className="text-xl font-bold text-textDark mb-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-textLight text-sm">
                      {item.description}
                    </p>
                  </div>
                </Link>
              ))}
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border-2 border-blue-100">
          <h3 className="text-2xl font-bold text-textDark mb-4 flex items-center gap-3">
            <span className="text-3xl">💡</span>
            เคล็ดลับสำหรับผู้สอนมือใหม่
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h4 className="font-bold text-textDark mb-2">1. เริ่มต้นง่ายๆ</h4>
              <p className="text-sm text-textLight">
                สร้างคอร์สแรกของคุณด้วยเนื้อหาที่คุณถนัด ไม่จำเป็นต้องสมบูรณ์แบบตั้งแต่แรก
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h4 className="font-bold text-textDark mb-2">2. ตั้งค่าโปรไฟล์</h4>
              <p className="text-sm text-textLight">
                โปรไฟล์ที่สมบูรณ์จะช่วยสร้างความน่าเชื่อถือและดึงดูดนักเรียน
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h4 className="font-bold text-textDark mb-2">3. ใช้รูปภาพคุณภาพสูง</h4>
              <p className="text-sm text-textLight">
                หน้าปกและรูปโปรไฟล์ที่ดูดีจะช่วยเพิ่มความน่าสนใจของคอร์ส
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h4 className="font-bold text-textDark mb-2">4. ติดตามสถิติ</h4>
              <p className="text-sm text-textLight">
                ใช้ Analytics เพื่อดูว่านักเรียนมีพฤติกรรมการเรียนอย่างไร
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-12 text-center">
          <Link
            to="/instructor/courses/new"
            className="inline-flex items-center gap-3 bg-gradient-primary text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            <PlusCircleIcon className="w-6 h-6" />
            สร้างคอร์สแรกของคุณเลย
          </Link>
        </div>
      </div>
    </div>
  );
}
